import { useCallback, useEffect, useRef, useState } from 'react';
import { CloudUpload } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type { PresignedRawUploadFile } from '../store/hooks';
import { usePresignRawUploadsMutation } from '../store/hooks';

interface UploadFile {
  file: File;
  path: string;
}

interface OrganizationDataImportV2SectionProps {
  organizationId: string;
  /** Called after a successful upload so the imports list can refresh. */
  onUploaded?: () => void;
  /**
   * When set, append the files to this existing batch (raw upload id) instead
   * of creating a new one. Allowed only while the batch is still pre-conversion.
   */
  uploadId?: string;
  /** Render just the drop zone, without the surrounding Card chrome (e.g. in a dialog). */
  embedded?: boolean;
}

/** How many direct-to-S3 PUTs to run at once (folder drops can be hundreds of files). */
const MAX_CONCURRENT_UPLOADS = 4;

interface UploadProgress {
  completed: number;
  total: number;
  percent: number;
}

const isFileEntry = (e: FileSystemEntry): e is FileSystemFileEntry => e.isFile;
const isDirectoryEntry = (e: FileSystemEntry): e is FileSystemDirectoryEntry =>
  e.isDirectory;

/** Read every entry of a directory (readEntries returns at most ~100/call). */
function readAllEntries(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const acc: FileSystemEntry[] = [];
    const readBatch = (): void => {
      reader.readEntries(batch => {
        if (batch.length === 0) {
          resolve(acc);
        } else {
          acc.push(...batch);
          readBatch();
        }
      }, reject);
    };
    readBatch();
  });
}

/** Recursively flatten a dropped file/folder entry into files + relative paths. */
async function entryToFiles(
  entry: FileSystemEntry,
  prefix: string,
): Promise<UploadFile[]> {
  if (isFileEntry(entry)) {
    const file = await new Promise<File>((resolve, reject) =>
      entry.file(resolve, reject),
    );
    return [{ file, path: `${prefix}${file.name}` }];
  }
  if (isDirectoryEntry(entry)) {
    const entries = await readAllEntries(entry.createReader());
    const groups = await Promise.all(
      entries.map(e => entryToFiles(e, `${prefix}${entry.name}/`)),
    );
    return groups.flat();
  }
  return [];
}

/**
 * PUT a single file's bytes directly to its presigned S3 URL. Uses
 * XMLHttpRequest (not fetch) so we can report real upload progress. Sends ONLY
 * the presigned headers + the body — no auth header (the URL carries its auth).
 */
function putFileToS3(
  target: PresignedRawUploadFile,
  file: File,
  onProgress: (loaded: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', target.url, true);
    for (const [name, value] of Object.entries(target.headers)) {
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = event => {
      if (event.lengthComputable) onProgress(event.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    xhr.send(file);
  });
}

/** Run async tasks with a bounded concurrency pool, preserving call order. */
async function runWithConcurrency(
  tasks: Array<() => Promise<void>>,
  limit: number,
): Promise<void> {
  let next = 0;
  const workers = Array.from(
    { length: Math.min(limit, tasks.length) },
    async () => {
      while (next < tasks.length) {
        const index = next;
        next += 1;
        await tasks[index]();
      }
    },
  );
  await Promise.all(workers);
}

/**
 * Raw source-file upload for the migration agent. Drag & drop files or a whole
 * folder (or browse) — each file is uploaded straight to the S3 landing zone
 * (v2 raw prefix) via a presigned PUT, so the bytes never stream through the app
 * server (which used to time out on large files). Folder structure is preserved.
 * What happens to an upload afterward (convert → stage → commit) shows in the
 * Imports list below.
 */
export function OrganizationDataImportV2Section({
  organizationId,
  onUploaded,
  uploadId,
  embedded = false,
}: OrganizationDataImportV2SectionProps) {
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [presignRawUploads] = usePresignRawUploadsMutation();

  // `webkitdirectory` isn't a typed React attribute — set it imperatively.
  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '');
      folderInputRef.current.setAttribute('directory', '');
    }
  }, []);

  const uploadItems = useCallback(
    async (items: UploadFile[]) => {
      if (items.length === 0 || !organizationId) return;
      setUploading(true);
      setProgress({ completed: 0, total: items.length, percent: 0 });
      try {
        // 1) Ask the BE for a presigned PUT URL per file.
        const presign = await presignRawUploads({
          organization_id: organizationId,
          files: items.map(item => ({ filename: item.path })),
          ...(uploadId ? { upload_id: uploadId } : {}),
        }).unwrap();

        // 2) Pair each returned URL with its File (matched by relative path).
        const byPath = new Map(items.map(item => [item.path, item.file]));
        const targets = (presign.files ?? [])
          .map(meta => ({ meta, file: byPath.get(meta.filename) }))
          .filter(
            (t): t is { meta: PresignedRawUploadFile; file: File } =>
              t.file !== undefined,
          );

        // 3) PUT each file's bytes directly to S3, tracking real progress.
        const totalBytes = targets.reduce((sum, t) => sum + t.file.size, 0);
        const loadedByKey = new Map<string, number>();
        const failures: string[] = [];
        let completed = 0;

        const reportProgress = () => {
          const loaded = [...loadedByKey.values()].reduce((a, b) => a + b, 0);
          const percent =
            totalBytes > 0 ? Math.round((loaded / totalBytes) * 100) : 100;
          setProgress({ completed, total: targets.length, percent });
        };

        await runWithConcurrency(
          targets.map(t => async () => {
            try {
              await putFileToS3(t.meta, t.file, loaded => {
                loadedByKey.set(t.meta.key, loaded);
                reportProgress();
              });
              loadedByKey.set(t.meta.key, t.file.size);
            } catch {
              failures.push(t.meta.filename);
            } finally {
              completed += 1;
              reportProgress();
            }
          }),
          MAX_CONCURRENT_UPLOADS,
        );

        // 4) Surface the outcome and refresh the uploads list.
        const succeeded = targets.length - failures.length;
        const skipped = presign.skipped?.length ?? 0;
        const verb = uploadId ? 'Added' : 'Uploaded';

        if (failures.length > 0) {
          enqueueSnackbar(
            `${verb} ${succeeded} of ${targets.length} file(s); ${failures.length} failed`,
            { variant: 'error' },
          );
        } else {
          enqueueSnackbar(
            skipped
              ? `${verb} ${succeeded} file(s), skipped ${skipped} duplicate(s)`
              : `${verb} ${succeeded} file(s)`,
            { variant: skipped ? 'warning' : 'success' },
          );
        }
        onUploaded?.();
      } catch (err) {
        enqueueSnackbar(err instanceof Error ? err.message : 'Upload failed', {
          variant: 'error',
        });
      } finally {
        setUploading(false);
        setProgress(null);
      }
    },
    [organizationId, uploadId, enqueueSnackbar, onUploaded, presignRawUploads],
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const dt = e.dataTransfer;
    // Grab entries synchronously — they become invalid after the handler ends.
    const entries: FileSystemEntry[] = [];
    for (const item of Array.from(dt.items)) {
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
    }
    const plainFiles = Array.from(dt.files);
    void (async () => {
      let collected: UploadFile[];
      if (entries.length > 0) {
        const groups = await Promise.all(entries.map(en => entryToFiles(en, '')));
        collected = groups.flat();
      } else {
        collected = plainFiles.map(file => ({ file, path: file.name }));
      }
      await uploadItems(collected);
    })();
  };

  const onPicked = (list: FileList | null) => {
    if (!list) return;
    const collected = Array.from(list).map(file => ({
      file,
      path: file.webkitRelativePath || file.name,
    }));
    void uploadItems(collected);
  };

  const dropZone = (
    <>
      <input
        ref={folderInputRef}
        type="file"
        hidden
        onChange={e => {
          onPicked(e.target.files);
          e.target.value = '';
        }}
      />

      <Box
        onDragOver={e => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={e => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={onDrop}
        sx={{
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          bgcolor: dragActive ? 'action.hover' : 'transparent',
        }}
      >
        <CloudUpload color="action" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag &amp; drop files or a folder here
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={
              uploading ? <CircularProgress size={16} /> : <CloudUpload />
            }
            disabled={uploading || !organizationId}
            onClick={() => folderInputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Choose files'}
          </Button>
        </Box>

        {uploading && progress && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress.percent} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block' }}
            >
              Uploading {progress.completed}/{progress.total} files…{' '}
              {progress.percent}%
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );

  if (embedded) {
    return dropZone;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Upload source files"
        subheader="Drop raw source files or a folder — each is uploaded straight to S3 (no processing, no app-server streaming). The migration agent converts them, then the result shows in Imports below."
      />
      <CardContent>{dropZone}</CardContent>
    </Card>
  );
}
